import api from './api';
import type { BoardPrice } from './deviceBoards';
import type { HardwareOrder, HardwareOrderDetail } from './orders';

// The buyer side of the marketplace: what a software provider can purchase
// (published SKUs with prices) and their order history.

export interface StoreVariant {
  id: number;
  board_id: number;
  name: string;
  sku: string;
  headline: string | null;
  status: 'available' | 'coming-soon' | 'discontinued';
  hero_asset_id: number | null;
  current_price: (BoardPrice & { inherited?: boolean }) | null;
}

export interface StoreBoard {
  board_id: number;
  board: string;
  display_name: string | null;
  headline: string | null;
  listing_slug: string | null;
  variants: StoreVariant[];
}

export interface StoreProvider {
  manufacturer_id: number;
  name: string;
  slug: string;
  tagline: string | null;
  /** Whether this provider extends terms (net billing) as a payment option. */
  offers_terms: boolean;
  boards: StoreBoard[];
}

export interface CheckoutAddress {
  name: string;
  company?: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal: string;
  country?: string;
  phone?: string;
}

export async function getStoreCatalog(): Promise<{ providers: StoreProvider[] }> {
  const response = await api.get('/store/catalog');
  return response.data;
}

export interface StoreAsset {
  id: number;
  kind: 'image' | 'document';
  title: string | null;
  file_name: string;
  variant_id: number | null;
}

export interface StoreProductDetail extends StoreVariant {
  manufacturer_offers_terms?: boolean;
  description: string | null;
  specs: { group: string; label: string; value: string }[];
  board: string;
  board_display_name: string | null;
  board_headline: string | null;
  board_description: string | null;
  manufacturer: string;
  manufacturer_id: number;
  provider_name: string;
  provider_slug: string;
  images: StoreAsset[];
  documents: StoreAsset[];
}

export async function getStoreProduct(variantId: number): Promise<StoreProductDetail> {
  const response = await api.get(`/store/products/${variantId}`);
  return response.data;
}

// ── Cart (client-side; persisted per browser as an unsent draft) ────────────

export interface CartItem {
  variantId: number;
  boardId: number;
  sku: string;
  name: string;
  providerName: string;
  manufacturerId: number;
  providerOffersTerms: boolean;
  quantity: number;
  unitPriceCents: number;
  monthlyFeeCents: number;
  currency: string;
  heroAssetId: number | null;
}

const CART_KEY = 'simkura-store-cart';

export function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveCart(items: CartItem[]): void {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch {
    // Storage unavailable — cart just won't survive a reload.
  }
}

export interface CheckoutResult {
  orders: PlaceOrderResult[];
}

export async function checkoutCart(data: {
  items: { variantId: number; quantity: number }[];
  paymentMethod: 'card' | 'terms';
  shipping: CheckoutAddress;
  billing?: CheckoutAddress | null;
  saveAsDefault?: boolean;
}): Promise<CheckoutResult> {
  const response = await api.post('/store/checkout', data);
  return response.data;
}

export interface PlaceOrderResult {
  order: HardwareOrder;
  payment: {
    charged: boolean;
    reason: string | null;
  };
}

/** Retry a pending card-payment order's charge. Reuses the checkout payment shape. */
export async function retryOrderPayment(orderId: number): Promise<PlaceOrderResult> {
  const response = await api.post(`/store/orders/${orderId}/retry-payment`);
  return response.data;
}

/** Buyer cancels their own still-pending order (card or terms). */
export async function cancelOrder(orderId: number): Promise<HardwareOrder> {
  const response = await api.post(`/store/orders/${orderId}/cancel`);
  return response.data;
}

export async function getStoreOrders(): Promise<{ orders: HardwareOrder[]; total: number }> {
  const response = await api.get('/store/orders');
  return response.data;
}

export async function getStoreOrder(orderId: number): Promise<HardwareOrderDetail> {
  const response = await api.get(`/store/orders/${orderId}`);
  return response.data;
}

/** Human copy for a checkout that couldn't charge. */
export function paymentReasonCopy(reason: string | null): string {
  switch (reason) {
    case 'terms':
      return 'Placed on terms — awaiting the hardware provider’s approval.';
    case 'payments_not_configured':
      return 'Payments are not enabled on this environment yet — the order is recorded as pending.';
    case 'no_payment_method':
      return 'No card on file — add one under Purchases → Payment methods. The order is recorded as pending.';
    case 'charge_failed':
      return 'The card on file could not be charged — the order is recorded as pending.';
    default:
      return reason
        ? `Payment did not complete (${reason.replace('payment_', '')}) — the order is recorded as pending.`
        : '';
  }
}
